import { useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { api, type ProfileResult } from '../api/client'
import { useAuth } from '../store/auth'
import { useSettings } from '../store/settings'
import { usePullAll } from '../store/pullAll'
import { useRefresh } from '../store/refresh'
import { useTabs } from '../store/tabs'
import { normalizeHandle } from '../utils/handle'
import { blobUrl } from '../utils/handle'
import { clearAllForHandle, db } from '../cache/db'
import { ChevronDown, ChevronUp, Cog, Menu } from './Icon'
import { ImageLightbox, type LightboxImage } from './ImageLightbox'
import { MobileMenu } from './MobileMenu'

type Section = 'tabs' | 'profile' | 'subnav'

const SUBNAV_SLOT_ID = 'topbar-subnav-slot'

export function TopBar({ onOpenSettings }: { onOpenSettings: () => void }) {
  const [collapsed, setCollapsed] = useState<Record<Section, boolean>>({
    tabs: false,
    profile: false,
    subnav: false,
  })
  const [menuOpen, setMenuOpen] = useState(false)
  const toggle = (s: Section) => setCollapsed((c) => ({ ...c, [s]: !c[s] }))

  return (
    <>
      <div className="sticky top-0 z-40 px-3 pt-3 sm:px-4 sm:pt-4">
        <div
          className="mx-auto max-w-6xl overflow-hidden rounded-3xl border shadow-glass backdrop-blur-xl"
          style={{
            background: 'rgba(8, 14, 32, 0.86)',
            borderColor: 'rgba(56, 189, 248, 0.22)',
          }}
        >
          <SearchRow onOpenMenu={() => setMenuOpen(true)} onOpenSettings={onOpenSettings} />
          <TabsRow collapsed={collapsed.tabs} onToggle={() => toggle('tabs')} />
          <ProfileRow collapsed={collapsed.profile} onToggle={() => toggle('profile')} />
          <SubnavRow collapsed={collapsed.subnav} onToggle={() => toggle('subnav')} />
        </div>
      </div>
      <MobileMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onOpenSettings={onOpenSettings}
      />
    </>
  )
}

/* ───────── Section header (clickable bar that toggles) ───────── */

function SectionHeader({
  collapsed,
  onToggle,
  label,
  count,
  children,
}: {
  collapsed: boolean
  onToggle: () => void
  label: string
  count?: number | string
  children?: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[11px] uppercase tracking-widest text-ink-300 transition hover:bg-white/5 sm:px-4"
      aria-expanded={!collapsed}
    >
      <span className="flex items-center gap-2">
        <span>{label}</span>
        {typeof count !== 'undefined' ? (
          <span className="rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[9px] text-ink-200">
            {count}
          </span>
        ) : null}
        {children ? <span className="hidden text-ink-400 sm:inline">{children}</span> : null}
      </span>
      {collapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
    </button>
  )
}

/* ───────── Search row (always visible) ───────── */

function SearchRow({
  onOpenMenu,
  onOpenSettings,
}: {
  onOpenMenu: () => void
  onOpenSettings: () => void
}) {
  const { handle: routeHandle } = useParams()
  const [handle, setHandle] = useState(routeHandle ?? '')
  const navigate = useNavigate()
  const location = useLocation()
  const session = useAuth((s) => s.session)
  const service = useSettings((s) => s.service)
  const lastTab = useTabs((s) => s.lastTab)

  useEffect(() => {
    if (routeHandle) setHandle(routeHandle)
  }, [routeHandle])

  function navTo(path: string, active: boolean) {
    navigate(active ? '/' : path)
  }

  const onCompare = location.pathname.startsWith('/compare')
  const onCorrelate = location.pathname.startsWith('/correlate')
  const onAnalysis = location.pathname.startsWith('/analysis')
  const onBulk = location.pathname.startsWith('/bulk')
  const onLurker = location.pathname.startsWith('/lurker')

  return (
    <div className="flex items-center gap-2 px-3 py-2.5 sm:px-4 sm:py-3">
      <Link to="/" title="home" className="flex flex-shrink-0 items-center gap-2">
        <span className="grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-gradient-to-br from-sky-400/40 to-violet-500/40 text-base font-black">
          b
        </span>
        <span className="hidden font-extrabold tracking-tight md:inline">
          bskydisc<span className="accent-text">o</span>mbobulate
        </span>
      </Link>

      <form
        className="flex min-w-0 flex-1 items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          const h = normalizeHandle(handle, service)
          if (h) navigate(`/profile/${h}/${lastTab || 'posts'}`)
        }}
      >
        <input
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          placeholder="handle, did, or you.bsky.social"
          className="min-w-0 flex-1 rounded-full border border-white/10 bg-black/30 px-4 py-2 text-sm text-ink-100 placeholder:text-ink-400 focus:border-[color:var(--accent-strong)] focus:outline-none"
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
        />
        <button
          type="submit"
          className="flex-shrink-0 rounded-full px-4 py-2 text-sm font-medium text-white transition active:scale-[0.97]"
          style={{
            background: 'var(--accent-strong)',
            boxShadow: '0 0 0 1px rgba(255,255,255,0.1) inset',
          }}
        >
          view
        </button>
      </form>

      {/* Desktop nav (lg+). Mobile uses the hamburger menu. */}
      <div className="hidden flex-shrink-0 items-center gap-1 lg:flex">
        <NavPill to="/compare" active={onCompare} onClick={() => navTo('/compare', onCompare)} label="compare" emoji="⇌" />
        <NavPill to="/correlate" active={onCorrelate} onClick={() => navTo('/correlate', onCorrelate)} label="AI" emoji="⊜" />
        <NavPill to="/analysis" active={onAnalysis} onClick={() => navTo('/analysis', onAnalysis)} label="analysis" emoji="🛡" />
        <NavPill to="/bulk" active={onBulk} onClick={() => navTo('/bulk', onBulk)} label="bulk" emoji="⊕" />
        <NavPill to="/lurker" active={onLurker} onClick={() => navTo('/lurker', onLurker)} label="lurker" emoji="⌖" />
      </div>

      {session ? (
        <span
          className="hidden flex-shrink-0 items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-ink-200 md:inline-flex"
          title={`signed in as @${session.handle}`}
        >
          <span className="h-2 w-2 rounded-full accent-bg" />
          <span className="max-w-[8rem] truncate">@{session.handle}</span>
        </span>
      ) : null}

      <button
        type="button"
        onClick={onOpenSettings}
        title="settings"
        aria-label="settings"
        className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full border border-white/10 bg-white/5 text-ink-100 transition hover:border-[color:var(--accent-strong)] hover:bg-white/10 hover:text-white"
      >
        <Cog />
      </button>

      <button
        type="button"
        onClick={onOpenMenu}
        title="menu"
        aria-label="open menu"
        className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full border border-white/10 bg-white/5 text-ink-100 transition hover:border-[color:var(--accent-strong)] hover:bg-white/10 hover:text-white lg:hidden"
      >
        <Menu />
      </button>
    </div>
  )
}

function NavPill({
  to,
  active,
  onClick,
  label,
  emoji,
}: {
  to: string
  active: boolean
  onClick: () => void
  label: string
  emoji: string
}) {
  return (
    <Link
      to={active ? '/' : to}
      onClick={(e) => {
        e.preventDefault()
        onClick()
      }}
      className={`rounded-full border px-3 py-1.5 text-xs transition ${
        active
          ? 'border-[color:var(--accent-strong)] bg-white/10 text-white'
          : 'border-white/10 bg-white/5 text-ink-200 hover:bg-white/10'
      }`}
      title={active ? `close ${label}` : label}
    >
      {active ? `✕ ${label}` : `${emoji} ${label}`}
    </Link>
  )
}

/* ───────── Tabs row (open profiles) ───────── */

function TabsRow({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const tabs = useTabs((s) => s.tabs)
  const active = useTabs((s) => s.active)
  const closeTab = useTabs((s) => s.close)
  const clearAll = useTabs((s) => s.clear)
  const lastTab = useTabs((s) => s.lastTab)
  const navigate = useNavigate()
  const location = useLocation()

  if (tabs.length === 0) return null

  function close(e: React.MouseEvent, h: string) {
    e.preventDefault()
    e.stopPropagation()
    const next = closeTab(h)
    if (location.pathname.toLowerCase().startsWith(`/profile/${h}`) || active === h) {
      if (next) navigate(`/profile/${next}/${lastTab || 'posts'}`)
      else navigate('/')
    }
  }

  return (
    <section className="border-t border-white/5">
      <SectionHeader collapsed={collapsed} onToggle={onToggle} label="tabs" count={tabs.length} />
      {collapsed ? null : (
        <div className="flex items-center gap-2 overflow-x-auto px-3 pb-2 sm:px-4">
          {tabs.map((h) => {
            const isActive = active === h
            return (
              <Link
                key={h}
                to={`/profile/${h}/${lastTab || 'posts'}`}
                className={`group flex flex-shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition ${
                  isActive
                    ? 'border-[color:var(--accent-strong)] bg-white/10 text-white'
                    : 'border-white/10 bg-white/5 text-ink-200 hover:bg-white/10'
                }`}
                title={`@${h}`}
              >
                <TabAvatar handle={h} />
                <span className="hidden max-w-[10rem] truncate sm:inline">@{h}</span>
                <span className="max-w-[6rem] truncate sm:hidden">
                  @{h.split('.')[0]}
                </span>
                <button
                  type="button"
                  onClick={(e) => close(e, h)}
                  aria-label={`close ${h} tab`}
                  className="rounded-full px-1 text-[10px] text-ink-300 opacity-60 transition hover:bg-white/10 hover:text-white hover:opacity-100"
                >
                  ✕
                </button>
              </Link>
            )
          })}
          {tabs.length > 1 ? (
            <button
              type="button"
              onClick={() => {
                if (confirm(`Close all ${tabs.length} tabs?`)) {
                  clearAll()
                  navigate('/')
                }
              }}
              className="ml-auto flex-shrink-0 rounded-full px-2 py-1 text-[11px] text-ink-400 hover:text-ink-200"
              title="close every tab"
            >
              close all
            </button>
          ) : null}
        </div>
      )}
    </section>
  )
}

function TabAvatar({ handle }: { handle: string }) {
  const [src, setSrc] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    db.profiles
      .where('handle')
      .equals(handle)
      .first()
      .then((p) => {
        if (cancelled || !p) return
        const av = (p.profile as { avatar?: { ref?: { $link?: string } | string } } | null)?.avatar
        const ref =
          typeof av === 'string'
            ? av
            : typeof av?.ref === 'string'
              ? av.ref
              : av?.ref?.$link
        if (ref) setSrc(blobUrl(p.did, ref, 'avatar'))
      })
    return () => {
      cancelled = true
    }
  }, [handle])
  if (src)
    return <img src={src} alt="" className="h-4 w-4 flex-shrink-0 rounded-full object-cover" />
  return (
    <span className="grid h-4 w-4 flex-shrink-0 place-items-center rounded-full bg-gradient-to-br from-sky-500/40 to-violet-500/40 text-[8px] font-bold">
      {handle.slice(0, 1).toUpperCase()}
    </span>
  )
}

/* ───────── Profile row (avatar + bio) ───────── */

function blobLink(b: unknown): string | null {
  if (!b) return null
  if (typeof b === 'string') return b
  const o = b as { ref?: unknown }
  if (typeof o.ref === 'string') return o.ref
  if (o.ref && typeof o.ref === 'object') {
    const r = o.ref as { $link?: string }
    if (typeof r.$link === 'string') return r.$link
  }
  return null
}

function ProfileRow({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const { handle: routeHandle } = useParams()
  const service = useSettings((s) => s.service)
  const cleanHandle = normalizeHandle(routeHandle ?? '', service)
  const bumpRefresh = useRefresh((s) => s.bump)
  const session = useAuth((s) => s.session)
  const pullAll = usePullAll()
  const startPull = usePullAll((s) => s.pull)
  const [profile, setProfile] = useState<ProfileResult | null>(null)
  const [lightbox, setLightbox] = useState<LightboxImage | null>(null)
  const [spinning, setSpinning] = useState(false)
  function onRefresh() {
    setSpinning(true)
    bumpRefresh()
    setTimeout(() => setSpinning(false), 700)
  }
  function onPullEverything() {
    if (!cleanHandle) return
    if (pullAll.state.running) {
      usePullAll.getState().show()
      return
    }
    startPull(cleanHandle, service, session?.accessJwt)
  }
  async function onResetAndRepull() {
    if (!cleanHandle) return
    if (pullAll.state.running) {
      alert('A pull is already running. Cancel it first.')
      return
    }
    if (
      !confirm(
        `Wipe ALL cached records, media, and hydration for @${cleanHandle} and re-pull everything from scratch?`,
      )
    ) {
      return
    }
    await clearAllForHandle(cleanHandle)
    bumpRefresh()
    startPull(cleanHandle, service, session?.accessJwt)
  }
  const isPulling = pullAll.state.running && pullAll.state.handle === cleanHandle

  useEffect(() => {
    if (!cleanHandle) {
      setProfile(null)
      return
    }
    let cancelled = false
    api
      .profile(cleanHandle, service)
      .then((p) => {
        if (!cancelled) setProfile(p)
      })
      .catch(() => {
        if (!cancelled) setProfile(null)
      })
    return () => {
      cancelled = true
    }
  }, [cleanHandle, service])

  if (!cleanHandle || !profile) return null

  const avatarLink = blobLink(profile.profile?.avatar)
  const avatarUrl = avatarLink ? blobUrl(profile.did, avatarLink, 'avatar') : undefined
  const description = profile.profile?.description
  const displayName = profile.profile?.displayName || profile.handle

  return (
    <section className="relative border-t border-white/5">
      <SectionHeader collapsed={collapsed} onToggle={onToggle} label={`@${profile.handle}`} />
      {/* Action cluster lives OUTSIDE the collapse conditional so refresh /
          pull / reset are reachable even when the bio section is folded
          shut. Pinned to the section's top-right corner. */}
      <div className="absolute right-10 top-1.5 z-10 flex items-center gap-1.5 sm:right-12">
        <button
          type="button"
          onClick={onRefresh}
          title="refresh latest for every tab on this profile"
          aria-label="refresh latest"
          className="grid h-7 w-7 place-items-center rounded-full border border-white/10 bg-white/10 text-ink-100 transition hover:border-[color:var(--accent-strong)] hover:bg-white/20 hover:text-white"
        >
          <svg
            viewBox="0 0 24 24"
            className={`h-3.5 w-3.5 ${spinning ? 'animate-spin' : ''}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 12a9 9 0 0 1 15.5-6.36L21 8" />
            <path d="M21 3v5h-5" />
            <path d="M21 12a9 9 0 0 1-15.5 6.36L3 16" />
            <path d="M3 21v-5h5" />
          </svg>
        </button>
        <button
          type="button"
          onClick={onPullEverything}
          title={
            isPulling
              ? 'show pull-everything progress'
              : 'pull EVERYTHING (posts, reposts, likes, follows, blocks, lists, packs, feeds) in parallel'
          }
          aria-label="pull everything"
          className="grid h-7 w-7 place-items-center rounded-full border text-white transition active:scale-[0.97]"
          style={{
            background: isPulling ? 'var(--accent-strong)' : 'var(--accent-soft)',
            borderColor: 'var(--accent-strong)',
          }}
        >
          <svg
            viewBox="0 0 24 24"
            className={`h-3.5 w-3.5 ${isPulling ? 'animate-pulse' : ''}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" />
          </svg>
        </button>
        <button
          type="button"
          onClick={onResetAndRepull}
          title="WIPE all cached data for this handle and re-pull everything from scratch"
          aria-label="reset cache and re-pull everything"
          className="grid h-7 w-7 place-items-center rounded-full border border-rose-400/40 bg-rose-500/15 text-rose-100 transition hover:border-rose-300 hover:bg-rose-500/30 hover:text-white"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 6h18" />
            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M12 11v6" />
          </svg>
        </button>
      </div>
      {collapsed ? null : (
        <div className="flex items-start gap-3 px-3 pb-3 pr-12 sm:px-4 sm:pb-4 sm:pr-14">
          {avatarUrl ? (
            <button
              type="button"
              onClick={() =>
                setLightbox({
                  src: avatarUrl,
                  alt: `@${profile.handle} avatar`,
                  downloadName: `${profile.handle}-avatar.jpg`,
                  postUrl: `https://bsky.app/profile/${profile.handle}`,
                })
              }
              className="flex-shrink-0"
              title="open avatar full-size"
            >
              <img
                src={avatarUrl}
                alt=""
                className="h-12 w-12 rounded-full border-2 border-white/30 object-cover transition hover:scale-105"
              />
            </button>
          ) : (
            <div className="h-12 w-12 flex-shrink-0 rounded-full border-2 border-white/30 bg-gradient-to-br from-sky-500/40 to-violet-500/40" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <a
                href={`https://bsky.app/profile/${profile.handle}`}
                target="_blank"
                rel="noreferrer"
                className="truncate font-bold accent-text hover:underline"
              >
                {displayName}
              </a>
              <span className="truncate text-xs text-ink-300">@{profile.handle}</span>
            </div>
            {description ? (
              <p className="mt-1 max-w-3xl whitespace-pre-wrap text-xs text-ink-100">
                {description}
              </p>
            ) : null}
            <div
              className="mt-1 truncate font-mono text-[10px] text-ink-400"
              title={profile.did}
            >
              {profile.did}
            </div>
          </div>
        </div>
      )}
      {lightbox ? <ImageLightbox image={lightbox} onClose={() => setLightbox(null)} /> : null}
    </section>
  )
}

/* ───────── Subnav (collection tabs portal) ───────── */

function SubnavRow({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  return (
    <section className="border-t border-white/5">
      <SectionHeader collapsed={collapsed} onToggle={onToggle} label="feed" />
      <div id={SUBNAV_SLOT_ID} className={collapsed ? 'hidden' : ''} />
    </section>
  )
}

export function SubnavPortal({ children }: { children: ReactNode }) {
  const [el, setEl] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setEl(document.getElementById(SUBNAV_SLOT_ID))
  }, [])
  if (!el) return null
  return createPortal(children, el)
}
